import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ChevronDown, Lightbulb, CheckCircle2 } from 'lucide-react';
import { useT } from '@/lib/i18n';

/**
 * AI Insight card — AI Clip Intelligence V3.
 *
 * A single collapsible, discreet card (Linear / Raycast / Vercel vibe) that
 * surfaces the LLM's own rationale for THIS clip. Contains up to three sections:
 *
 *   1. Pourquoi ce clip ?           → clip.reasons        (max 5 bullets)
 *   2. Sous-scores                   → hook / rewatch / share (0-100 bars)
 *   3. Comment l'améliorer           → clip.suggestions    (max 3 bullets)
 *
 * The card is:
 *   • strictly additive — never affects viral_score
 *   • fully silent when NO new field is populated (renders nothing)
 *   • collapsible, first-visit-open, memorised per clip id in localStorage
 *   • never shows placeholders — a section disappears when its data is absent
 */
const STORAGE_KEY = 'hl.aiInsight.collapsed';

const readCollapsedMap = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};
const writeCollapsedMap = (map) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / private mode
  }
};

const SubScoreBar = ({ label, value }) => {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="flex items-center gap-3" data-testid={`ai-insight-subscore-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className="text-[11px] uppercase tracking-wider text-zinc-500 w-28 shrink-0">{label}</span>
      <div className="flex-1 h-[3px] bg-white/[0.06] rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${v}%` }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="h-full bg-[#D4FF00]/70 rounded-full"
        />
      </div>
      <span className="font-mono text-[11px] text-zinc-400 w-8 text-right tabular-nums">{v}</span>
    </div>
  );
};

const AIInsightCard = ({ clip }) => {
  const { lang } = useT();

  const reasons = Array.isArray(clip?.reasons) ? clip.reasons.filter(Boolean).slice(0, 5) : [];
  const suggestions = Array.isArray(clip?.suggestions) ? clip.suggestions.filter(Boolean).slice(0, 3) : [];

  const hook = typeof clip?.hook_quality === 'number' ? clip.hook_quality : null;
  const rewatch = typeof clip?.rewatch_potential === 'number' ? clip.rewatch_potential : null;
  const share = typeof clip?.share_potential === 'number' ? clip.share_potential : null;

  const hasReasons = reasons.length > 0;
  const hasSuggestions = suggestions.length > 0;
  const hasSubScores = hook !== null || rewatch !== null || share !== null;
  const hasAnything = hasReasons || hasSuggestions || hasSubScores;

  const clipId = clip?.id;
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    if (!clipId) return;
    const map = readCollapsedMap();
    // First time we see this clip → open by default. After that → obey user's stored choice.
    if (map[clipId] === undefined) {
      setCollapsed(false);
      map[clipId] = false;
      writeCollapsedMap(map);
    } else {
      setCollapsed(!!map[clipId]);
    }
  }, [clipId]);

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      const map = readCollapsedMap();
      if (clipId) {
        map[clipId] = next;
        writeCollapsedMap(map);
      }
      return next;
    });
  };

  const labels = useMemo(() => (lang === 'fr' ? {
    title: 'AI Insight',
    why: 'Pourquoi ce clip ?',
    scores: 'Sous-scores',
    improve: 'Comment l\'améliorer',
    hook: 'Hook quality',
    rewatch: 'Rewatch potential',
    share: 'Share potential',
    show: 'Afficher',
    hide: 'Masquer',
  } : {
    title: 'AI Insight',
    why: 'Why this clip?',
    scores: 'Sub-scores',
    improve: 'How to improve it',
    hook: 'Hook quality',
    rewatch: 'Rewatch potential',
    share: 'Share potential',
    show: 'Show',
    hide: 'Hide',
  }), [lang]);

  if (!hasAnything) return null;

  return (
    <div
      className="mt-6 rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden"
      data-testid="ai-insight-card"
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 group"
        data-testid="ai-insight-toggle"
        aria-expanded={!collapsed}
      >
        <div className="flex items-center gap-2.5">
          <span className="w-6 h-6 rounded-md bg-[#D4FF00]/10 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-[#D4FF00]" strokeWidth={2.2} />
          </span>
          <span className="text-sm font-medium text-zinc-200 tracking-tight">{labels.title}</span>
        </div>
        <motion.div animate={{ rotate: collapsed ? 0 : 180 }} transition={{ duration: 0.25 }}>
          <ChevronDown className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 transition-colors" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 space-y-5 border-t border-white/5 pt-5">
              {hasReasons && (
                <section data-testid="ai-insight-reasons">
                  <h4 className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2.5">{labels.why}</h4>
                  <ul className="space-y-1.5">
                    {reasons.map((r, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-300 leading-snug">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#D4FF00] mt-0.5 shrink-0" strokeWidth={2.2} />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {hasSubScores && (
                <section data-testid="ai-insight-subscores">
                  <h4 className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2.5">{labels.scores}</h4>
                  <div className="space-y-2">
                    {hook !== null && <SubScoreBar label={labels.hook} value={hook} />}
                    {rewatch !== null && <SubScoreBar label={labels.rewatch} value={rewatch} />}
                    {share !== null && <SubScoreBar label={labels.share} value={share} />}
                  </div>
                </section>
              )}

              {hasSuggestions && (
                <section data-testid="ai-insight-suggestions">
                  <h4 className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2.5">{labels.improve}</h4>
                  <ul className="space-y-1.5">
                    {suggestions.map((s, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-300 leading-snug">
                        <Lightbulb className="w-3.5 h-3.5 text-zinc-500 mt-0.5 shrink-0" strokeWidth={2} />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIInsightCard;
